import { BUSINESS_INFO, BUSINESS_SAME_AS, SITE_URL } from "../config";
import { normalizeImageUrl } from "./imageUrl";

const DEFAULT_LOGO = `${SITE_URL}/logo.jpg`;
const DEFAULT_IMAGE = DEFAULT_LOGO;
const DEFAULT_DESCRIPTION =
  "Cưng Home chuyên sofa, giường bọc nệm và nội thất phòng khách may đo theo yêu cầu, tư vấn thiết kế, giao hàng và lắp đặt tại nhà.";

function absoluteUrl(pathOrUrl = "") {
  const normalized = normalizeImageUrl(pathOrUrl);
  if (/^https?:\/\//i.test(normalized)) return normalized;
  if (!normalized) return SITE_URL;
  return `${SITE_URL}${normalized.startsWith("/") ? "" : "/"}${normalized}`;
}

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    element.setAttribute(key, String(value));
  });
}

function upsertLink(selector, attributes) {
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("link");
    document.head.appendChild(element);
  }

  Object.entries(attributes).forEach(([key, value]) => {
    if (value === null || value === undefined) return;
    element.setAttribute(key, String(value));
  });
}

function upsertJsonLd(id, payload) {
  let element = document.head.querySelector(`#${id}`);

  if (!element) {
    element = document.createElement("script");
    element.id = id;
    element.type = "application/ld+json";
    document.head.appendChild(element);
  }

  element.textContent = JSON.stringify(payload);
}

export function setSeo({
  title,
  description = DEFAULT_DESCRIPTION,
  canonicalPath = "/",
  image = DEFAULT_IMAGE,
  type = "website",
  jsonLd,
}) {
  const canonical = absoluteUrl(canonicalPath);
  const imageUrl = absoluteUrl(image);
  const pageTitle = title || `${BUSINESS_INFO.name} - Sofa Và Giường Bọc Nệm May Đo`;

  document.title = pageTitle;

  upsertMeta('meta[name="description"]', { name: "description", content: description });
  upsertMeta('meta[name="robots"]', { name: "robots", content: "index, follow" });
  upsertMeta('meta[property="og:title"]', { property: "og:title", content: pageTitle });
  upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
  upsertMeta('meta[property="og:type"]', { property: "og:type", content: type });
  upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
  upsertMeta('meta[property="og:image"]', { property: "og:image", content: imageUrl });
  upsertMeta('meta[property="og:image:secure_url"]', { property: "og:image:secure_url", content: imageUrl });
  upsertMeta('meta[name="twitter:card"]', { name: "twitter:card", content: "summary_large_image" });
  upsertMeta('meta[name="twitter:title"]', { name: "twitter:title", content: pageTitle });
  upsertMeta('meta[name="twitter:description"]', { name: "twitter:description", content: description });
  upsertMeta('meta[name="twitter:image"]', { name: "twitter:image", content: imageUrl });
  upsertLink('link[rel="canonical"]', { rel: "canonical", href: canonical });

  if (jsonLd) upsertJsonLd("page-structured-data", jsonLd);
}

export function setHomeSeo() {
  const description =
    "Cưng Home chuyên sofa, sofa chữ L, sofa bed, giường bọc nệm và nội thất phòng khách may đo theo yêu cầu.";

  setSeo({
    title: `${BUSINESS_INFO.name}, Giường Bọc Nệm Và Nội Thất Phòng Khách`,
    description,
    canonicalPath: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "FurnitureStore",
        "@id": `${SITE_URL}/#store`,
        name: BUSINESS_INFO.shortName,
        alternateName: BUSINESS_INFO.alternateNames,
        url: SITE_URL,
        logo: DEFAULT_LOGO,
        image: DEFAULT_IMAGE,
        telephone: BUSINESS_INFO.telephoneSchema,
        hasMap: BUSINESS_INFO.mapsUrl,
        address: {
          "@type": "PostalAddress",
          ...BUSINESS_INFO.address,
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: BUSINESS_INFO.coordinates.latitude,
          longitude: BUSINESS_INFO.coordinates.longitude,
        },
        priceRange: BUSINESS_INFO.priceRange,
        openingHoursSpecification: BUSINESS_INFO.openingHours.map((hour) => ({
          "@type": "OpeningHoursSpecification",
          dayOfWeek: hour.day,
          opens: hour.opens,
          closes: hour.closes,
        })),
        contactPoint: {
          "@type": "ContactPoint",
          contactType: "Customer Service",
          telephone: BUSINESS_INFO.telephoneSchema,
          url: SITE_URL,
        },
        areaServed: BUSINESS_INFO.areaServed.map((name) => ({
          "@type": "Place",
          name,
        })),
        serviceArea: BUSINESS_INFO.areaServed.map((name) => ({
          "@type": "Place",
          name,
        })),
        hasOfferCatalog: {
          "@type": "OfferCatalog",
          name: "Sofa & Nội Thất Phòng Khách",
          itemListElement: [
            {
              "@type": "Offer",
              name: "Sofa Băng May Đo",
              priceCurrency: "VND",
              price: "1000000",
              priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Sofa Chữ L May Đo",
              priceCurrency: "VND",
              price: "2000000",
              priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              availability: "https://schema.org/InStock",
            },
            {
              "@type": "Offer",
              name: "Giường Bọc Nệm May Đo",
              priceCurrency: "VND",
              price: "3000000",
              priceValidUntil: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000)
                .toISOString()
                .split("T")[0],
              availability: "https://schema.org/InStock",
            },
          ],
        },
        knowsAbout: BUSINESS_INFO.knowsAbout,
        sameAs: BUSINESS_SAME_AS,
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "@id": `${SITE_URL}/#website`,
        name: BUSINESS_INFO.name,
        url: SITE_URL,
        publisher: {
          "@id": `${SITE_URL}/#store`,
        },
        potentialAction: {
          "@type": "SearchAction",
          target: `${SITE_URL}/?search={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      },
    ],
  });
}

function cleanText(value, fallback = "") {
  return String(value || fallback)
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function parsePrice(value) {
  const digits = String(value || "").replace(/[^\d]/g, "");
  if (!digits || /liên hệ/i.test(String(value || ""))) return null;
  return digits;
}

export function setProductSeo(product, slug) {
  if (!product) return;

  const name = cleanText(product.name, "Sản phẩm nội thất Cưng Home");
  const type = cleanText(product.type, "nội thất");
  const description = `${name} ${type} may đo tại xưởng Cưng Home, Thủ Đức, TP.HCM. Tùy chọn kích thước, màu và chất liệu; tư vấn tận nơi, bảo hành rõ ràng.`;
  const price = parsePrice(product.price);
  const canonicalPath = `/${slug || product.slug || product._id || ""}`;
  const images = [product.image, ...(Array.isArray(product.gallery) ? product.gallery : [])].filter(Boolean);
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    image: images.map(absoluteUrl),
    description,
    brand: {
      "@type": "Brand",
      name: BUSINESS_INFO.name,
    },
    category: type,
    url: absoluteUrl(canonicalPath),
  };

  if (price) {
    productSchema.offers = {
      "@type": "Offer",
      price,
      priceCurrency: "VND",
      availability: "https://schema.org/InStock",
      url: absoluteUrl(canonicalPath),
    };
  }

  setSeo({
    title: `${name} - ${type} May Đo | Cưng Home - Xưởng Sofa May Đo HCM`,
    description,
    canonicalPath,
    image: product.image || DEFAULT_IMAGE,
    type: "product",
    jsonLd: productSchema,
  });
}
